/**
 * Plan from a photo — web (docs/CAPTURE_TO_PLAN_PLAN.md, slice 2).
 *
 * `CaptureButton` sits beside the plans-chat textarea; `CaptureIdeasPanel`
 * renders in the conversation. Both read `useCaptureStore`.
 *
 * - The button shows only after the config probe says `enabled: true`.
 * - Signed out (Decision 4): no file input is rendered at all, a tap opens
 *   the "Sign up to plan from a photo" prompt, and nothing is captured.
 * - Signed in: the picker, the client-side caps from the probe, then the
 *   capture request. "Plan it" starts a fresh new-plan chat entry whose
 *   query is the idea's server-composed `request_text` — the only text a
 *   pick ever sends (Decision 3). Highlights are display-only.
 */
import React, { useEffect, useRef } from 'react';
import { useUserStore } from '../../stores/useUserStore';
import { useCaptureStore } from '../../stores/useCaptureStore';
import {
  captureIdeas,
  getCaptureConfig,
  PlansChatApiError,
  type CaptureIdea,
} from '../../services/plansChatApi';
import {
  trackCaptureFailed,
  trackCaptureIdeaPlanned,
  trackCaptureIdeasFound,
  trackCaptureSignUpPrompted,
  type CaptureFailureReason,
} from '../../hooks/useAnalytics';
import type { PlanChatEntry } from '../../hooks/usePlanChat';
import { newUuid } from '../../utils/uuid';
import './CaptureIdeas.css';

export const SIGN_UP_PROMPT_COPY = 'Sign up to plan from a photo';
export const EMPTY_IDEAS_COPY = 'Try a page that names a place — '
  + 'or just tell me where it made you want to go.';
export const PRIVACY_COPY = "VizDay doesn't keep your photo. It's sent to Google's Gemini to "
  + "read, under Google's API terms.";

/**
 * The results header, worded as the plan prescribes (§1): "I found N trips
 * in this", with its N=1 and N=0 forms.
 */
export function resultsHeading(count: number): string {
  if (count === 0) return "I didn't find a trip in this";
  if (count === 1) return 'I found a trip in this';
  return `I found ${count} trips in this`;
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

/**
 * Every failure the capture route can answer, as one of the §7 reasons.
 * By status first: DRF's 403 and 429 carry `{detail}`, not a code.
 */
export function captureFailureReason(err: unknown): CaptureFailureReason {
  if (!(err instanceof PlansChatApiError)) return 'error';
  switch (err.status) {
    case 404: return 'unavailable';
    case 429: return 'rate_limited';
    case 413: return 'too_large';
    case 400:
      if (err.message === 'too_many_sources' || err.message === 'source_too_large') {
        return 'too_large';
      }
      if (err.message === 'unsupported_source') return 'unsupported';
      return 'error';
    default: return 'error';
  }
}

const megabytes = (bytes: number) => Math.floor(bytes / (1024 * 1024));

export function failureCopy(
  reason: CaptureFailureReason, maxSources: number, maxBytesEach: number,
): string {
  switch (reason) {
    case 'unavailable':
      return "Adding a photo isn't available right now.";
    case 'rate_limited':
      // Neutral on purpose: a 429 here can come from the shared AI throttle,
      // not only from this route's own photo limit.
      return "I can't take another request right now. Try again a little later.";
    case 'too_large':
      return `That's more than I can read at once: up to ${maxSources} photos, `
        + `${megabytes(maxBytesEach)} MB each.`;
    case 'unsupported':
      return "I couldn't read that file. Try a JPEG, PNG or WebP photo.";
    default:
      return 'Something went wrong reading your photo. Give it another try in a moment.';
  }
}

// One probe at a time; a failed probe leaves the button hidden and is
// retried the next time a button mounts.
let configProbe: Promise<void> | null = null;
function probeConfig(): void {
  if (configProbe) return;
  configProbe = getCaptureConfig()
    .then((config) => { useCaptureStore.getState().setConfig(config); })
    .catch(() => { /* hidden until a later probe answers */ })
    .finally(() => { configProbe = null; });
}

/** Signed in, files chosen: the caps, then the request. */
export async function runCapture(files: File[]): Promise<void> {
  const store = useCaptureStore.getState();
  const config = store.config;
  if (!config || files.length === 0) return;
  if (
    files.length > config.max_sources
    || files.some((file) => file.size > config.max_bytes_each)
  ) {
    store.rejectLocally('too_large');
    trackCaptureFailed('too_large');
    return;
  }
  const request = store.begin();
  try {
    const response = await captureIdeas(files);
    if (useCaptureStore.getState().applyCaptureResponse(request, response)) {
      trackCaptureIdeasFound(
        files.length,
        Array.isArray(response.ideas) ? response.ideas.length : 0,
        response.source_kind,
      );
    }
  } catch (err) {
    const reason = captureFailureReason(err);
    if (useCaptureStore.getState().applyCaptureFailure(request, reason)) {
      trackCaptureFailed(reason);
    }
  }
}

const CameraIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

interface CaptureButtonProps {
  disabled?: boolean;
  /** The chat is open. The probe waits for it, so a page that merely
   *  mounts a closed overlay makes no request. */
  active?: boolean;
}

export const CaptureButton: React.FC<CaptureButtonProps> = ({
  disabled = false, active = true,
}) => {
  const config = useCaptureStore((s) => s.config);
  const status = useCaptureStore((s) => s.status);
  const setSignUpPrompt = useCaptureStore((s) => s.setSignUpPrompt);
  const user = useUserStore((s) => s.user);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (active && !config) probeConfig();
  }, [active, config]);

  if (!config?.enabled) return null;

  if (!user) {
    return (
      <button
        type="button"
        className="capture-button"
        aria-label="Add a photo"
        title="Add a photo"
        onClick={() => setSignUpPrompt(true)}
      >
        <CameraIcon />
      </button>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        className="capture-file-input"
        data-testid="capture-file-input"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          // Let the same file be picked again after a failure.
          e.target.value = '';
          void runCapture(files);
        }}
      />
      <button
        type="button"
        className="capture-button"
        aria-label="Add a photo"
        title="Add a photo"
        disabled={disabled || status === 'loading'}
        onClick={() => inputRef.current?.click()}
      >
        <CameraIcon />
      </button>
    </>
  );
};

const TRIP_PHRASE: Record<CaptureIdea['trip_type'], string> = {
  city: 'City trip',
  beach: 'Beach trip',
  nature: 'Nature trip',
  road_trip: 'Road trip',
  cruise: 'Cruise',
  resort: 'Resort stay',
  multi_city: 'Multi-city trip',
  other: 'Trip',
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function ideaMeta(idea: CaptureIdea): string {
  const parts: string[] = [TRIP_PHRASE[idea.trip_type] ?? 'Trip'];
  if (idea.days != null) parts.push(`${idea.days} ${idea.days === 1 ? 'day' : 'days'}`);
  if (idea.month != null && MONTHS[idea.month - 1]) parts.push(MONTHS[idea.month - 1]);
  else if (idea.season && idea.season !== 'any') {
    parts.push(idea.season.charAt(0).toUpperCase() + idea.season.slice(1));
  }
  return parts.join(' · ');
}

/**
 * What the panel is showing, or null when it shows nothing. The panel
 * reveals itself on each change, and the chat overlay re-anchors the panel
 * in the transcript on each change (`PlanChatOverlay`).
 */
export type CaptureView = 'prompt' | 'loading' | `failed:${CaptureFailureReason}` | 'done';

export function useCaptureView(): CaptureView | null {
  return useCaptureStore((s) => (
    s.signUpPrompt ? 'prompt'
      : s.status === 'loading' ? 'loading'
        : s.status === 'failed' && s.failure ? `failed:${s.failure}` as CaptureView
          : s.status === 'done' ? 'done'
            : null
  ));
}

interface CaptureIdeasPanelProps {
  /** The chat's own `reset` — "Plan it" starts a fresh new-plan entry. */
  reset: (entry?: PlanChatEntry) => void;
  onNavigate?: (path: string) => void;
}

export const CaptureIdeasPanel: React.FC<CaptureIdeasPanelProps> = ({ reset, onNavigate }) => {
  const {
    config, status, ideas, planned, failure, signUpPrompt,
    setSignUpPrompt, markPlanned, clear,
  } = useCaptureStore();

  // What the panel is showing. Each change is revealed: the panel sits in a
  // scrolling transcript, and a prompt, a wait, a failure or a result that
  // lands out of view is one the reader never sees. Keyed on the view only:
  // later chat turns render below the panel and are the transcript's to
  // reveal, so a new message must not pull the reader back up here.
  const view = useCaptureView();
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!view) return;
    panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [view]);

  const handleSignUp = () => {
    setSignUpPrompt(false);
    trackCaptureSignUpPrompted('sign_up');
    onNavigate?.('/login');
  };
  const handleNotNow = () => {
    setSignUpPrompt(false);
    trackCaptureSignUpPrompted('dismissed');
  };

  const handlePlanIt = (idea: CaptureIdea, index: number) => {
    trackCaptureIdeaPlanned(ideas.length, idea.trip_type, idea.season, idea.days != null);
    markPlanned(index);
    reset({
      intent: 'new_plan',
      entryId: newUuid(),
      query: idea.request_text,
      source: 'capture',
    });
  };

  if (signUpPrompt) {
    return (
      <div ref={panelRef} className="capture-panel" role="group" aria-label="Plan from a photo">
        <p className="capture-prompt-title">{SIGN_UP_PROMPT_COPY}</p>
        <div className="capture-actions">
          <button type="button" className="capture-primary" onClick={handleSignUp}>
            Sign up
          </button>
          <button type="button" className="capture-secondary" onClick={handleNotNow}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div ref={panelRef} className="capture-panel" role="status">
        <p className="capture-status">Reading your photo…</p>
        <p className="capture-note">{PRIVACY_COPY}</p>
      </div>
    );
  }

  if (status === 'failed' && failure) {
    return (
      <div ref={panelRef} className="capture-panel" role="alert">
        <p className="capture-error">
          {failureCopy(failure, config?.max_sources ?? 4, config?.max_bytes_each ?? 5242880)}
        </p>
        <div className="capture-actions">
          <button type="button" className="capture-secondary" onClick={clear}>Dismiss</button>
        </div>
      </div>
    );
  }

  if (status !== 'done') return null;

  return (
    <div ref={panelRef} className="capture-panel" role="group" aria-label="Trips from your photo">
      <div className="capture-panel-header">
        <h3 className="capture-heading">{resultsHeading(ideas.length)}</h3>
        <button type="button" className="capture-secondary" onClick={clear}>Clear</button>
      </div>
      {ideas.length === 0 ? (
        <p className="capture-status">{EMPTY_IDEAS_COPY}</p>
      ) : (
        <ul className="capture-ideas">
          {ideas.map((idea, index) => (
            <li
              key={`${idea.destination}-${index}`}
              className={`capture-idea${planned.includes(index) ? ' is-planned' : ''}`}
            >
              <div className="capture-idea-body">
                <span className="capture-idea-destination">{idea.destination}</span>
                <span className="capture-idea-meta">{ideaMeta(idea)}</span>
                {Array.isArray(idea.highlights) && idea.highlights.length > 0 && (
                  <span className="capture-idea-highlights">{idea.highlights.join(' · ')}</span>
                )}
              </div>
              <button
                type="button"
                className="capture-primary"
                onClick={() => handlePlanIt(idea, index)}
              >
                Plan it
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="capture-note">{PRIVACY_COPY}</p>
    </div>
  );
};
